import type { RegistrationSurveyCode } from './types';

/** How often the session heartbeat re-checks the stored expiry. */
export const UPDATE_INTERVAL = 10 * 1000;

/**
 * Treat a session as dead this long before its stated `expiresAt`, so a request
 * cannot go out with a token that dies in flight. Small on purpose: survey-api
 * has **no refresh endpoint** — the token is opaque and the session lives
 * server-side — so this margin is time cut off the session, not time to renew in.
 */
export const EXPIRY_SKEW = 30 * 1000;

export const STORAGE_KEYS = {
  SESSION: 'veritant.session',
  REDIRECT_URL: 'veritant.redirect_url',
  REGISTRATION_RUN: 'veritant.registration_run',
  VERIFICATION_PROMPT_DISMISSED: 'veritant.verification_prompt_dismissed'
} as const;

export const LOGIN_ROUTE = '/login';
/** Where Invent's IdP sends the browser back, with a code to exchange or, after a redeem, the
 *  address to sign in with — `{origin}` + this path is the redirect URI the IdP's portal
 *  registers per origin. Anonymous and outside both zones: one sign-in serves two portals. */
export const AUTH_CALLBACK_ROUTE = '/auth/callback';
/** Asking for an invitation to the IdP. Anonymous by necessity: asking is what somebody who
 *  cannot sign in does. A sibling of `/login`, never behind `ProtectedRoute`. */
export const SIGN_IN_REQUEST_ROUTE = '/sign-in/request';
/** Anonymous, and more so than the request: a registrant has no account until the run's last
 *  step creates it. The run it drives is never in this path — see `registrationRunStorage`. */
export const REGISTRATION_ROUTE = '/registration';
/** The catalogue — `/my/cycles`, the Advisor Portal's My surveys. Named here, with the portal's
 *  prefix spelled as `HELP_ROUTE` spells it, because a registration inside an account lands on it
 *  with router state, and `/`, which lands by kind, would be a second navigation that drops the
 *  state. The survey domain's `PORTAL_PATHS.surveys` is this constant, not a second spelling. */
export const CATALOGUE_ROUTE = '/portal/surveys';
/** Registration inside an account — the same page, signed in, adding a survey to the account
 *  rather than making one (the teams onboarding plan's R7). Under the catalogue, inside the
 *  Advisor Portal's zone, whose element turns a Team Portal account away at its door;
 *  `/registration` with a session is sent here. */
export const ADD_SURVEY_ROUTE = `${CATALOGUE_ROUTE}/add`;
/** The query parameter that picks a registration's survey on arrival — a survey code, which
 *  names nobody, unlike the run id nothing puts in a URL. `/registration` keeps it on its way
 *  to `ADD_SURVEY_ROUTE`. */
export const REGISTRATION_SURVEY_PARAM = 'survey';
/** `ADD_SURVEY_ROUTE` opened on one survey's run — how the catalogue sends a team routed to the
 *  RIA firm survey to register the firm. */
export function addSurveyRoute(survey: RegistrationSurveyCode): string {
  return `${ADD_SURVEY_ROUTE}?${REGISTRATION_SURVEY_PARAM}=${survey}`;
}
/** Where survey-api's address confirmation mail links, token in the fragment — its
 *  `Mail:WebBaseUrl` + this path, which the two repos have to agree on. Anonymous: the link is
 *  opened wherever the mail is read, and a confirmation proves an address without needing the
 *  account it belongs to. */
export const EMAIL_VERIFY_ROUTE = '/email/verify';
/** Where survey-api's point-of-contact invitation links (SR-416), token in the fragment — the
 *  same reasons: the mail is opened wherever it is read, and whoever it reached may have no
 *  account here yet. */
export const INVITATION_ACCEPT_ROUTE = '/invitations/accept';
/** Where a bulk mail's footer links (SR-523), token in the fragment — survey-api's
 *  `Mail:WebBaseUrl` + this path. Anonymous: whoever reads the mail may have no account. */
export const UNSUBSCRIBE_ROUTE = '/unsubscribe';
/** The Advisor Portal's Help — anonymous as well, outside `ProtectedRoute`,
 *  because registration's refusals point at it before any account exists. Signed in, it is drawn
 *  in the portal's shell; signed out, at the front door (the two-portals design, §10 ruling 3). */
export const HELP_ROUTE = '/portal/help';
/** Resolved by the account's kind, not a page of its own: an Advisor Portal account lands on the
 *  Advisor Portal, a Team Portal account on the Team Portal (`RoleLanding`). */
export const HOME_ROUTE = '/';
